import { TimerMode } from '@/components/settings/timer-configuration/types'
import { useAppSelector } from '@/redux/hooks'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import { GameRoomStatus } from '@/redux/types/room'

export const useGamePhase = () => {
  const { status } = useAppSelector((state) => state.timerConfiguration)

  const gameMode = status?.mode || TimerMode.SINGLE_ROUND
  const manualMode = status?.mode === TimerMode.MANUAL_MODE
  const currentRound = status?.currentRound || 0
  const totalRounds = status?.totalRounds || 1
  const timeRemaining = status?.timeRemaining || 0
  const roundNumber = status?.roundData?.roundNumber || 0

  let currentPhase = null
  let startTime = null
  let endTime = null
  let phaseDuration = null
  let phaseStatus = null
  let phaseId = null
  let phasesData = null
  let randrPhase = null
  let advisorPhase = null
  let negotiationPhase = null
  let decisionPhase = null
  let isInfinitePhase = false

  if (gameMode === TimerMode.MANUAL_MODE) {
    phasesData = status?.phasesData || []
    randrPhase = phasesData?.[0]
    advisorPhase = phasesData?.[1]
    negotiationPhase = phasesData?.[2]
    decisionPhase = phasesData?.[3]
  } else {
    currentPhase = status?.phaseData?.phaseType || null
    startTime = status?.phaseData?.startTime || null
    endTime = status?.phaseData?.endTime || null
    phaseDuration = status?.phaseData?.duration || null
    phaseStatus = status?.phaseData?.status || null
    phaseId = status?.phaseData?.id || null
    isInfinitePhase = status?.phaseData?.isInfinite || false
  }

  const isRandRPhase = currentPhase === 'randr' || !!phasesData?.[0]?.isActive
  const isAdvisorPhase =
    currentPhase === 'advisor' || !!phasesData?.[1]?.isActive
  const isNegotiationPhase =
    currentPhase === 'negotiation' || !!phasesData?.[2]?.isActive
  const isDecisionPhase =
    currentPhase === 'decision' || !!phasesData?.[3]?.isActive

  const isMultiRound = totalRounds > 1
  const isGameStarted = currentRound > 0
  const isGameCompleted = status?.mode === ActivityState.Completed
  const roundId = status?.roundData?.id || null

  // Paused detection: the game-level status from the timer payload is
  // authoritative when present. The nested round status can be absent or stale
  // (e.g. a pause issued while no phase was active stamps nothing on the
  // round), which left the UI showing "Pause" for an already-paused game.
  const rawRoundStatus = status?.roundData?.status || null
  const gameStatus = status?.gameStatus ?? null
  let roundStatus = rawRoundStatus
  if (gameStatus === GameRoomStatus.PAUSED) {
    roundStatus = ActivityState.Paused
  } else if (gameStatus !== null && rawRoundStatus === ActivityState.Paused) {
    // Backend says the game is NOT paused — the nested paused status is stale.
    roundStatus = ActivityState.InProgress
  }

  const canGoPreviousPhase = status?.canGoPreviousPhase || false

  return {
    currentPhase,
    currentRound,
    roundNumber,
    totalRounds,
    isRandRPhase,
    isAdvisorPhase,
    isNegotiationPhase,
    isDecisionPhase,
    timeRemaining,
    isMultiRound,
    isGameStarted,
    isGameCompleted,
    phaseId,
    roundId,
    startTime,
    phaseDuration,
    phaseStatus,
    roundStatus,
    canGoPreviousPhase,
    gameMode,
    manualMode,
    phasesData,
    randrPhase,
    advisorPhase,
    negotiationPhase,
    decisionPhase,
    isInfinitePhase,
    endTime,
  }
}
